import { noop } from 'lodash'
import { createAsyncAction } from 'typesafe-actions'

import { ReduxActionCallbacks } from 'packages/grimoire/src/utils'
import { DateFormat, formatLocalized } from 'packages/utils/dateHelpers'
import { RequestConfig, RequestOptions } from 'packages/utils/store'

import { setIsCoverageStale } from '../../coverage/actions'
import { crossCoverageService } from '../crossCoverage.service'
import {
  NormalizedCrossCoverageApiResponse,
  CrossCoverageActionTypes,
  CrossCoveragePostAPIAttributes,
  CrossCoveragePatchData,
} from '../crossCoverage.types'

export const updateCrossCoverageAction = createAsyncAction(
  CrossCoverageActionTypes.UPDATE_CROSS_COVERAGE,
  CrossCoverageActionTypes.UPDATE_CROSS_COVERAGE_SUCCESS,
  CrossCoverageActionTypes.UPDATE_CROSS_COVERAGE_FAILURE,
)<
  RequestConfig<NormalizedCrossCoverageApiResponse>,
  NormalizedCrossCoverageApiResponse,
  Error
>()

export const buildPayloadFrom = (
  patchData: CrossCoveragePatchData,
): RequestOptions => {
  const {
    endDate,
    endTimeLocal,
    friday,
    isRepeating,
    monday,
    saturday,
    startDate,
    startTimeLocal,
    sunday,
    thursday,
    tuesday,
    tz,
    userCoveredId,
    userCoveringId,
    wednesday,
  } = patchData

  const attributes: Partial<CrossCoveragePostAPIAttributes> = {
    end_date: formatLocalized(endDate, DateFormat.ApiUtcShort),
    end_time_local: `${endTimeLocal}:00`,
    friday,
    is_repeating: isRepeating,
    monday,
    saturday,
    start_date: formatLocalized(startDate, DateFormat.ApiUtcShort),
    start_time_local: `${startTimeLocal}:00`,
    sunday,
    thursday,
    tuesday,
    tz: tz,
    wednesday,
  }

  const userFrom = (id: string) => ({ data: { id, type: 'user' } })

  const relationships = {
    user_covered: userFrom(userCoveredId),
    user_covering: userFrom(userCoveringId),
  }

  return {
    data: {
      attributes,
      relationships,
      type: 'cross_coverage',
    },
  }
}

export const updateCrossCoverage =
  (
    id: string,
    patchData: CrossCoveragePatchData,
    callbacks: ReduxActionCallbacks = {},
  ) =>
  async dispatch => {
    const { onError = noop, onSuccess = noop } = callbacks

    try {
      const payload = buildPayloadFrom(patchData)
      const request = crossCoverageService.updateCrossCoverage.bind(
        null,
        id,
        payload,
      )
      const result = await dispatch(
        updateCrossCoverageAction.request({ request }),
      )

      dispatch(updateCrossCoverageAction.success(result))
      dispatch(setIsCoverageStale(true))

      onSuccess()
      return result.normalized
    } catch (error) {
      await dispatch(updateCrossCoverageAction.failure(error))
      onError(error)
      throw error
    }
  }
